import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Save01 } from 'untitledui-js-base';
import { useCommonApi } from '../../api';
import { PATHS } from '../../paths';
import { SeznamAutocomplete } from '../../types/external/seznamAutocomplete';
import { formatSeznamAddress } from '../../utils/formatSeznamAddress';
import { AutocompleteInput } from '../common/AutocompleteInput';
import { getAccountingYears } from '../sourceCommissions/utils';
import { ACCIDENT_SOURCE, AccidentSourceValueItem } from '../types/accident';
import { RegistryRequest } from '../types/registry';
import { DialogAccidentList } from './DialogAccidentList';
import { RegistryAccidentsFilter } from './type';

const ACCOUNTING_YEARS = getAccountingYears(new Date().getFullYear());

export type TipperFormValues = Omit<RegistryRequest, 'type'> & { type?: ACCIDENT_SOURCE };

interface TipperDialogProps extends Omit<DialogProps, 'onSubmit'> {
  mode: 'create' | 'edit';
  initialData?: AccidentSourceValueItem;
  onSubmit?: (formData: TipperFormValues, typeChanged: boolean) => void;
  isLoading?: boolean;
  autocompleteResults?: SeznamAutocomplete[];
  onAutocompleteSearch?: (value: string) => void;
  isLoadingAddressData?: boolean;
}

export const TipperDialog = ({
  open,
  onClose,
  mode,
  autocompleteResults,
  onAutocompleteSearch,
  isLoadingAddressData,
  initialData,
  onSubmit,
  isLoading
}: TipperDialogProps) => {
  const navigate = useNavigate();

  const [commissionPaid, setCommissionPaid] = useState<'' | 'true' | 'false'>('');
  const [accountingYear, setAccountingYear] = useState<'' | number>('');
  const accidentsFilter = useMemo<RegistryAccidentsFilter>(
    () => ({
      commissionPaid: commissionPaid === '' ? undefined : commissionPaid,
      year: accountingYear === '' ? undefined : accountingYear
    }),
    [commissionPaid, accountingYear]
  );

  const { data: tipperDetail, isFetching: isFetchingAccidents } = useQuery({
    ...useCommonApi().getAccidentBySources(initialData?.id as number, accidentsFilter),
    enabled: !!initialData?.id,
    placeholderData: keepPreviousData
  });

  const [selectedYear, setSelectedYear] = useState<string>('');

  const selectedStatistics =
    tipperDetail?.statistics?.find((stat) => stat.year === selectedYear) ?? tipperDetail?.statistics?.[0];

  const methods = useForm<TipperFormValues>({
    defaultValues: initialData
      ? {
          ...initialData,
          name: initialData.personName ?? initialData.name,
          tradeName: initialData.tradeName ?? '',
          type: initialData.type as ACCIDENT_SOURCE
        }
      : {
          id: undefined,
          birthDate: '',
          phone: '',
          bankAccountNumber: '',
          name: '',
          tradeName: '',
          address: '',
          commissionValue: '',
          defaultCommissionPaymentType: '',
          type: ACCIDENT_SOURCE.TIPPER
        }
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = methods;

  useEffect(() => {
    reset(
      initialData
        ? {
            ...initialData,
            name: initialData.personName ?? initialData.name,
            tradeName: initialData.tradeName ?? '',
            type: initialData.type as ACCIDENT_SOURCE
          }
        : {
            id: undefined,
            birthDate: '',
            phone: '',
            bankAccountNumber: '',
            name: '',
            tradeName: '',
            address: '',
            commissionValue: '',
            defaultCommissionPaymentType: '',
            type: ACCIDENT_SOURCE.TIPPER
          }
    );
    setSelectedYear('');
    setCommissionPaid('');
    setAccountingYear('');
  }, [initialData, reset]);

  const initialType = initialData?.type as ACCIDENT_SOURCE | undefined;

  const title = mode === 'edit' ? `Upravit tipaře: ${initialData?.name ?? ''}` : 'Přidat nového tipaře';
  const submitLabel = mode === 'edit' ? 'Upravit tipaře' : 'Vytvořit tipaře';

  return (
    <Dialog
      open={!!open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose?.(event, reason);
      }}
      maxWidth="lg"
      slotProps={{
        paper: {
          sx: {
            minWidth: { xs: '100%', sm: 450 },
            width: { xs: '100%', sm: 'auto' },
            m: { xs: 0, sm: 3 }
          }
        }
      }}
    >
      <form onSubmit={handleSubmit((data) => onSubmit?.(data, initialType !== data.type))}>
        <Controller name="id" control={control} render={({ field }) => <input type="hidden" {...field} />} />
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Grid container mt={2} spacing={2}>
            {mode === 'edit' && (
              <Grid size={12}>
                <Controller
                  control={control}
                  name="type"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="type-label" shrink>
                        Typ záznamu
                      </InputLabel>
                      <Select
                        labelId="type-label"
                        value={value ?? ACCIDENT_SOURCE.TIPPER}
                        onChange={onChange}
                        label="Typ záznamu"
                        notched
                      >
                        <MenuItem value={ACCIDENT_SOURCE.TIPPER}>Tipař</MenuItem>
                        <MenuItem value={ACCIDENT_SOURCE.SERVICE}>Servis</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}
            <Grid size={12}>
              <Controller
                control={control}
                name="name"
                rules={{ required: 'Jméno je povinné' }}
                render={({ field: { onChange, value }, fieldState }) => (
                  <TextField
                    label="Jméno"
                    variant="outlined"
                    onChange={onChange}
                    value={value}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="tradeName"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Obchodní název"
                    variant="outlined"
                    onChange={onChange}
                    value={value ?? ''}
                    fullWidth
                    slotProps={{ htmlInput: { maxLength: 255 } }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <AutocompleteInput
                autocompleteItems={autocompleteResults ?? []}
                onSearch={onAutocompleteSearch}
                onSelect={(it) => setValue('address', formatSeznamAddress(it))}
                isLoading={isLoadingAddressData}
                formatSelected={(it) => `${it.data.name} ${it.data.location}`}
                clearOnClose={false}
                freeSolo
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name="address"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Adresa (ulice, PSČ, město)"
                    variant="outlined"
                    onChange={onChange}
                    value={value ?? ''}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="phone"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Telefon"
                    variant="outlined"
                    onChange={onChange}
                    value={value}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="birthDate"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label="Datum narození"
                    value={value ? parseISO(value) : null}
                    onChange={(date) => onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                    format="dd.MM.yyyy"
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        error: Boolean(errors.birthDate),
                        helperText: errors?.birthDate?.message
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="bankAccountNumber"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Číslo bankovního účtu"
                    variant="outlined"
                    onChange={onChange}
                    value={value}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="commissionValue"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Výše provize"
                    variant="outlined"
                    onChange={onChange}
                    value={value}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Zadejte hodnotu například 100 Kč nebo 20%"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="defaultCommissionPaymentType"
                render={({ field: { onChange, value } }) => (
                  <FormControl fullWidth>
                    <InputLabel id="payment-type-label" shrink>
                      Výchozí způsob vyplácení
                    </InputLabel>
                    <Select
                      labelId="payment-type-label"
                      value={value ?? ''}
                      onChange={onChange}
                      label="Výchozí způsob vyplácení"
                      displayEmpty
                      notched
                    >
                      <MenuItem value="">Nevybráno</MenuItem>
                      <MenuItem value="cash">Hotově</MenuItem>
                      <MenuItem value="online">Převodem</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
          {tipperDetail?.statistics && !isEmpty(tipperDetail?.statistics) && (
            <Box
              mt={3}
              p={2}
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                bgcolor: 'grey.100'
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography fontWeight="700">Statistiky</Typography>
                <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'white' }}>
                  <InputLabel id="year-select-label">Rok</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedStatistics?.year ?? ''}
                    label="Rok"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {tipperDetail.statistics.map((stat) => (
                      <MenuItem key={stat.year} value={stat.year}>
                        {stat.year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {selectedStatistics && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Celkem:</strong> {selectedStatistics.total}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Zbývající limit:</strong> {selectedStatistics.limitRemaining}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}
          {mode === 'edit' && initialData?.id && (
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} mt={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="commission-paid-label">Provize vyplacena</InputLabel>
                <Select
                  labelId="commission-paid-label"
                  value={commissionPaid}
                  label="Provize vyplacena"
                  onChange={(e) => setCommissionPaid(e.target.value as '' | 'true' | 'false')}
                >
                  <MenuItem value="">Vše</MenuItem>
                  <MenuItem value="true">Ano</MenuItem>
                  <MenuItem value="false">Ne</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="accounting-year-label">Účetní rok</InputLabel>
                <Select
                  labelId="accounting-year-label"
                  value={accountingYear}
                  label="Účetní rok"
                  onChange={(e) => setAccountingYear(e.target.value as '' | number)}
                >
                  <MenuItem value="">Vše</MenuItem>
                  {ACCOUNTING_YEARS.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}
          {tipperDetail?.items && isEmpty(tipperDetail.items) && !isFetchingAccidents && (
            <Typography mt={3} color="text.secondary">
              Žádné nehody neodpovídají filtru.
            </Typography>
          )}
          {tipperDetail?.items && !isEmpty(tipperDetail?.items) && (
            <DialogAccidentList accidents={tipperDetail?.items} onClick={(id) => navigate(PATHS.ACCIDENTS(id))} />
          )}
        </DialogContent>
        <DialogActions>
          <Stack mt={2} px={2} textAlign="center" width={1} gap={1}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<Save01 />}
              color="success"
              loading={isLoading}
            >
              {submitLabel}
            </Button>
            <Button variant="text" onClick={() => onClose?.({}, 'escapeKeyDown')}>
              Zavřít
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};
